import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { 
  Users, 
  Clock, 
  DollarSign, 
  Settings, 
  Split, 
  ArrowRight,
  X 
} from 'lucide-react';
import { Table, Order, TableStatus } from '../types';

interface TableManagementProps {
  table: Table;
  order?: Order;
  isOpen: boolean;
  onClose: () => void;
  onUpdateStatus: (tableId: string, status: TableStatus) => void;
  onSplitBill: (tableId: string) => void;
  onTransferItems: (tableId: string) => void;
}

export const TableManagement: React.FC<TableManagementProps> = ({
  table,
  order,
  isOpen,
  onClose,
  onUpdateStatus,
  onSplitBill,
  onTransferItems
}) => {
  const [showStatusOptions, setShowStatusOptions] = useState(false);

  if (!isOpen) return null;

  const getStatusColor = (status: TableStatus) => {
    switch (status) {
      case 'libre':
        return 'bg-green-100 text-green-800';
      case 'ocupada':
        return 'bg-red-100 text-red-800';
      case 'reservada':
        return 'bg-yellow-100 text-yellow-800';
      case 'limpieza':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getOccupationTime = () => {
    if (!table.occupiedSince) return '';
    
    const occupiedTime = new Date(table.occupiedSince);
    const now = new Date();
    const diffMs = now.getTime() - occupiedTime.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${diffHours}h ${diffMinutes}m`;
  };

  const handleStatusChange = (newStatus: TableStatus) => {
    onUpdateStatus(table.id, newStatus);
    setShowStatusOptions(false);
  };

  const statusOptions: TableStatus[] = ['libre', 'ocupada', 'reservada', 'limpieza'];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Mesa {table.number}</span>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
              Cerrar
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Información de la Mesa */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Información de Mesa
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span>Capacidad: {table.capacity} personas</span>
                <Badge className={getStatusColor(table.status)}>
                  Estado: {table.status}
                </Badge>
              </div>
              
              {table.occupiedSince && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="w-4 h-4" />
                  <span>Tiempo ocupada: {getOccupationTime()}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Orden Actual */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Orden Actual
              </CardTitle>
            </CardHeader>
            <CardContent>
              {order ? (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Total: S/ {order.total.toFixed(2)}</span>
                    <Badge variant="outline">{order.status}</Badge>
                  </div>
                  
                  <div className="space-y-2">
                    {order.items.map((item) => (
                      <div key={item.id} className="flex justify-between text-sm">
                        <span>{item.quantity}x {item.name}</span>
                        <span>S/ {item.total.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground">No hay orden activa</p>
              )}
            </CardContent>
          </Card>

          {/* Opciones de Gestión */}
          <div className="space-y-3">
            <h3 className="font-medium">Opciones de Gestión</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="relative">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setShowStatusOptions(!showStatusOptions)}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Cambiar Estado
                </Button>
                
                {showStatusOptions && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-md shadow-lg z-10">
                    {statusOptions.map((status) => (
                      <button
                        key={status}
                        className="w-full px-4 py-2 text-left hover:bg-gray-50 capitalize"
                        onClick={() => handleStatusChange(status)}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <Button
                variant="outline"
                onClick={() => onSplitBill(table.id)}
                disabled={!order}
              >
                <Split className="w-4 h-4 mr-2" />
                Dividir Cuenta
              </Button>

              <Button
                variant="outline"
                onClick={() => onTransferItems(table.id)}
                disabled={!order}
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Transferir Productos
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};